import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { CustomHttpClient } from '../../../services/custom-http-client.service';
import { UpdoxDirectMessage, MessageFolder } from './updox-direct-message';
import { UpdoxContact } from './updox-contact';
import { MessageAttachmentsResponse, DocumentType, CcdaParseResponse } from './updox-attachment';

export interface PatientDocument {
  id: number;
  name: string;
  date: string;
  document_type_name: string;
  file_name: string;
  content_type: string;
  file_size: number;
}

@Injectable()
export class UpdoxMessagesService {
  constructor(private http: CustomHttpClient) {}

  // --- Messages ---

  getMessages(practiceId: number, folder: MessageFolder, page: number = 1): Promise<{
    messages: any[];
    page: number;
    totalPages: number;
    totalCount: number;
  }> {
    const params = new HttpParams()
      .set('practice_id', String(practiceId))
      .set('folder', folder)
      .set('page', String(page));
    return this.http.getWithHeaders('updox/direct_messages', {
      observe: 'response',
      responseType: 'json',
      params,
    }).toPromise().then(resp => ({
      messages: (resp.body as any[]) || [],
      page:       Number(resp.headers.get('X-Page')        || page),
      totalPages: Number(resp.headers.get('X-Total-Pages') || 1),
      totalCount: Number(resp.headers.get('X-Total')       || 0),
    }));
  }

  sendMessage(message: UpdoxDirectMessage, files: File[] = [], chartDocumentIds: number[] = []): Promise<any> {
    if (files.length === 0 && chartDocumentIds.length === 0) {
      return this.http.post('updox/send_direct_message', message.to_send_params()).toPromise();
    }
    const fd = new FormData();
    const p = message.to_send_params().direct_message;
    Object.entries(p).forEach(([k, v]) => {
      if (v != null) { fd.append(`direct_message[${k}]`, String(v)); }
    });
    files.forEach(f => fd.append('attachments[]', f, f.name));
    chartDocumentIds.forEach(id => fd.append('chart_document_ids[]', String(id)));
    return this.http.post('updox/send_direct_message', fd).toPromise();
  }

  saveDraft(message: UpdoxDirectMessage): Promise<any> {
    return this.http.post('updox/draft', message.to_send_params()).toPromise();
  }

  updateDraft(id: number, message: UpdoxDirectMessage): Promise<any> {
    const params = new HttpParams().set('id', String(id));
    return this.http.put('updox/draft', message.to_send_params(), { params }).toPromise();
  }

  getMessage(id: number): Promise<any> {
    const params = new HttpParams().set('id', String(id));
    return this.http.get('updox/direct_message', { params }).toPromise();
  }

  markRead(messageId: number, read: boolean = true): Promise<any> {
    const params = new HttpParams()
      .set('id', String(messageId))
      .set('read', String(read));
    return this.http.put('updox/mark_read', {}, { params }).toPromise();
  }

  deleteMessage(messageId: number): Promise<any> {
    const params = new HttpParams().set('id', String(messageId));
    return this.http.delete('updox/message', { params }).toPromise();
  }

  // Link (or unlink with null) a patient to a message. Returns
  // { id, patient_id, patient: { id, name, complete_detail } | null }.
  linkPatient(messageId: number, patientId: number | null): Promise<any> {
    const params = new HttpParams().set('id', String(messageId));
    return this.http.put('updox/link_patient', { patient_id: patientId }, { params }).toPromise();
  }

  searchContacts(practiceId: number, query: string): Promise<{ wellwink_contacts: any[], directory_results: any[] }> {
    const params = new HttpParams()
      .set('practice_id', String(practiceId))
      .set('query', query);
    return this.http.get('updox/search_contacts', { params }).toPromise();
  }

  // --- Attachments ---

  getMessageAttachments(messageId: number): Promise<MessageAttachmentsResponse> {
    const params = new HttpParams().set('id', String(messageId));
    return this.http.get('updox/message_attachments', { params }).toPromise();
  }

  addToChart(body: {
    id: number;
    attachment_id: number;
    patient_id?: number;
    document_type_id?: number;
    name?: string;
    date?: string;
  }): Promise<any> {
    return this.http.post('updox/add_to_chart', body).toPromise();
  }

  parseCcda(
    messageId: number,
    source: { attachment_id?: number; metadata_index?: number },
  ): Promise<CcdaParseResponse> {
    let params = new HttpParams().set('id', String(messageId));
    if (source.attachment_id != null) {
      params = params.set('attachment_id', String(source.attachment_id));
    } else {
      params = params.set('metadata_index', String(source.metadata_index));
    }
    return this.http.get('updox/parse_ccda', { params }).toPromise();
  }

  getDocumentTypes(): Promise<DocumentType[]> {
    return this.http.get('documents/documents_type').toPromise();
  }

  // --- Patient search (practice-scoped, for compose/add-to-chart/C-CDA) ---

  searchPatients(query: string, practiceId: number): Promise<any[]> {
    const params = new HttpParams()
      .set('practice_id', String(practiceId))
      .set('name', query);
    return this.http.get('updox/search_patients', { params }).toPromise()
      .then((resp: any) => Array.isArray(resp) ? resp : []);
  }

  getPatientById(patientId: number, practiceId: number): Promise<any> {
    const params = new HttpParams()
      .set('practice_id', String(practiceId))
      .set('patient_id', String(patientId));
    return this.http.get('updox/search_patients', { params }).toPromise()
      .then((resp: any) => Array.isArray(resp) && resp.length > 0 ? resp[0] : null);
  }

  getPatientDocuments(patientId: number): Promise<PatientDocument[]> {
    const params = new HttpParams().set('patient_id', String(patientId));
    return this.http.get('updox/patient_documents', { params }).toPromise()
      .then((resp: any) => Array.isArray(resp) ? resp : []);
  }

  // --- Contacts ---

  getContacts(practiceId: number): Promise<any[]> {
    const params = new HttpParams().set('practice_id', String(practiceId));
    return this.http.get('updox/contacts', { params }).toPromise();
  }

  createContact(contact: UpdoxContact): Promise<any> {
    return this.http.post('updox/contact', contact.to_params()).toPromise();
  }

  updateContact(contact: UpdoxContact): Promise<any> {
    const params = new HttpParams().set('id', String(contact.id));
    return this.http.put('updox/contact', contact.to_params(), { params }).toPromise();
  }

  deleteContact(contactId: number): Promise<any> {
    const params = new HttpParams().set('id', String(contactId));
    return this.http.delete('updox/contact', { params }).toPromise();
  }
}
